// LinkedIn campaign landing pages for the vendor-screening offer.
// Product facts verified against ~/Projects/zc-vendor-check (the live engine).
// DO NOT overclaim: direct board verification exists for 6 states ONLY.
export const BOARD_STATES = ['Arizona', 'California', 'Florida', 'Illinois', 'Nevada', 'Texas'];

// Shared, verified product truths — reused across every page so claims can't drift.
export const CAPABILITIES = [
  {
    title: 'Counterparty red-flag screen',
    body: 'Lawsuits, judgments, liens, complaints, BBB records, regulatory actions, and news — graded low, medium, or high. Works for any company, in any industry.',
  },
  {
    title: 'Contractor license verification',
    body: `Direct state-board lookups in ${BOARD_STATES.join(', ')} — status, license number, issue and expiry dates, classifications, surety bond, workers' compensation. Other states return web-search pointers, which is research, not verification.`,
  },
  {
    title: 'A source URL on every claim',
    body: 'Nothing is inferred. If a lookup fails or a record is not found, it says "Lookup failed" or "Not found" — there are no mock fallbacks anywhere in the pipeline.',
  },
  {
    title: 'Coverage currency',
    body: 'License, bond, and workers-comp dates become a countdown, with a panel listing only what needs chasing — 30 days red, 60 days amber, past-date red. An uploaded certificate of insurance answers general liability, which no board publishes.',
  },
  {
    title: 'Re-checkable runs',
    body: 'Every run is timestamped, so re-screening the same vendor surfaces what changed: Active to Expired, a cancelled bond, a new disciplinary action.',
  },
  {
    title: 'One verdict',
    body: 'CLEAR, REVIEW, or DO NOT ENGAGE — with plain-English reasoning, the evidence behind it, and a checklist of what to obtain before you bid.',
  },
];

export const vendorPages = [
  {
    slug: 'background-check',
    shortTitle: 'Not a background check',
    eyebrow: 'Third-party & vendor risk screening',
    h1: 'The vendor questionnaire is not a background check.',
    sub: 'A self-reported form tells you what a vendor is willing to say about itself. It will not surface the judgment filed last spring, the license that lapsed in March, or the pattern in the complaints. Those live in public records nobody has time to read.',
    metaTitle: 'Vendor Questionnaire vs Background Check | UBOTIKA Vendor Screening',
    metaDescription:
      'Self-reported vendor questionnaires miss lawsuits, liens, lapsed licenses, and regulatory actions. UBOTIKA screens counterparties against public records and returns a sourced verdict in under a minute.',
    points: [
      'A questionnaire is the vendor grading its own homework.',
      'Judgments, liens, and regulatory actions do not appear unless somebody looks.',
      'A license that was valid at onboarding may not be valid today.',
    ],
    cta: 'Screen a vendor you already work with',
    ctaNote: 'Send one name. We run it and show you what a questionnaire would have missed.',
  },
  {
    slug: 'one-verdict',
    shortTitle: 'One verdict',
    eyebrow: 'Third-party & vendor risk screening',
    h1: 'Fourteen browser tabs, or one verdict.',
    sub: 'The state licensing board. PACER. The county recorder. BBB. Three news searches. Then the judgment call, from memory, at 4:50pm on a Friday. That is the process this replaces — not the judgment, the tab-opening.',
    metaTitle: 'Automated Vendor Screening | Replace Manual Due-Diligence Research',
    metaDescription:
      'Stop assembling vendor due diligence across a dozen tabs. UBOTIKA runs the license board, court, lien, complaint, and news checks in one pass and returns CLEAR, REVIEW, or DO NOT ENGAGE with every source linked.',
    points: [
      'One name in, one sourced verdict out — every finding graded and linked.',
      'The evidence stays attached, so a reviewer can audit the reasoning.',
      'A checklist of what to obtain before bidding comes with it.',
    ],
    cta: 'See a real screening report',
    ctaNote: 'We will run a live screen and walk you through the output.',
  },
  {
    slug: 'with-sources',
    shortTitle: 'Admits what it does not know',
    eyebrow: 'Third-party & vendor risk screening',
    h1: 'An AI screening tool that admits when it doesn\'t know.',
    sub: 'A fabricated "Active" is worse than an error. If a board lookup fails, the report says the lookup failed. If a record is not found, it says not found. There are no mock fallbacks anywhere in the pipeline, and every claim carries the URL it came from.',
    metaTitle: 'AI Vendor Screening With Sources | No Hallucinated Compliance Data',
    metaDescription:
      'Every claim carries its source URL. Failed lookups return "Lookup failed", never a guess. Direct state-board verification in Arizona, California, Florida, Illinois, Nevada, and Texas — stated plainly, not implied nationwide.',
    points: [
      'Every finding links to the record it came from.',
      '"Lookup failed" and "Not found" are real outputs, not edge cases we hid.',
      'Direct board verification in 6 states. Everywhere else is labelled as research, not verification.',
    ],
    cta: 'Audit our sourcing yourself',
    ctaNote: 'We will run a screen and you can click through every citation.',
    honest: true,
  },
  {
    slug: 'still-compliant',
    shortTitle: 'Still compliant?',
    eyebrow: 'Third-party & vendor risk screening',
    h1: 'Your vendor was compliant in March. Are they compliant now?',
    sub: 'Onboarding checks expire quietly. A license lapses, a bond gets cancelled, a disciplinary action lands — and nothing in your system changes, because nothing looked again.',
    metaTitle: 'Ongoing Vendor Monitoring | Catch Lapsed Licenses and Cancelled Bonds',
    metaDescription:
      'Onboarding checks go stale. UBOTIKA timestamps every vendor screen so re-checks surface what changed — Active to Expired, cancelled bonds, new disciplinary actions — with a 30/60-day coverage countdown and lapse alerts.',
    points: [
      'Runs are timestamped, so a re-check shows what changed since last time.',
      'Coverage countdown: 30 days red, 60 days amber, past-date red.',
      'An emailed digest when a license, bond, or workers-comp lapses.',
    ],
    cta: 'Re-screen your current vendor list',
    ctaNote: 'Send the list. We will tell you which ones changed since you onboarded them.',
  },
  {
    slug: '45-seconds',
    shortTitle: '45 seconds',
    eyebrow: 'Third-party & vendor risk screening',
    h1: 'A full vendor screen in 45 seconds.',
    sub: 'Fifteen to forty-five seconds from a company name to a sourced verdict. Fast enough to screen every counterparty instead of only the ones that already worried somebody.',
    metaTitle: 'Vendor Screening in 45 Seconds | Fast Counterparty Risk Checks',
    metaDescription:
      'A complete counterparty screen — license, litigation, liens, complaints, regulatory actions, news — in 15 to 45 seconds, with a CLEAR, REVIEW, or DO NOT ENGAGE verdict and every source linked.',
    points: [
      'Fast enough to screen everyone, not just the suspicious ones.',
      'Same depth as the manual process: license, courts, liens, complaints, news.',
      'The bottleneck stops being research and goes back to being your decision.',
    ],
    cta: 'Time it yourself',
    ctaNote: 'Give us a company name and we will send the report back the same day.',
  },
];

export const getVendorPage = (slug) => vendorPages.find((p) => p.slug === slug);
